/**
 * Tenant Utility Functions
 * Handles tenant ID sanitization to prevent non-ASCII characters
 */

/**
 * Sanitize tenant ID to remove Turkish and other special characters
 * Ensures tenant IDs only contain a-z, 0-9, hyphen, and underscore
 * 
 * Examples:
 * - "İtalia2" → "italia2"
 * - "Türkiye" → "turkiye"
 * - "Şirket-123" → "sirket-123"
 * - "Café" → "cafe"
 */
export function sanitizeTenantId(tenantId: string): string {
  if (!tenantId) return tenantId;
  
  // Step 1: Convert to lowercase
  let sanitized = tenantId.toLowerCase();
  
  // Step 2: Replace Turkish characters
  const turkishCharMap: Record<string, string> = {
    'ı': 'i',
    'i̇': 'i', // Dotted lowercase i
    'İ': 'i', // Uppercase dotted I
    'ş': 's',
    'Ş': 's',
    'ğ': 'g',
    'Ğ': 'g',
    'ü': 'u',
    'Ü': 'u',
    'ö': 'o',
    'Ö': 'o',
    'ç': 'c',
    'Ç': 'c',
  };
  
  // Replace Turkish characters
  for (const [turkish, latin] of Object.entries(turkishCharMap)) {
    sanitized = sanitized.split(turkish).join(latin);
  }

  // Step 2b: Kiril alfabesini Latin'e çevir (Rus/Ukrayna müşteriler Kiril klavye
  // kullanıyor; tenant id ASCII olmak zorunda olduğu için transliterasyon yapılır).
  // Örn: "химчистка" → "himchistka"
  const cyrillicMap: Record<string, string> = {
    'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'e','ж':'zh','з':'z','и':'i',
    'й':'y','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t',
    'у':'u','ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'sch','ъ':'','ы':'y','ь':'',
    'э':'e','ю':'yu','я':'ya','і':'i','ї':'yi','є':'e','ґ':'g',
  };
  sanitized = sanitized.replace(/[а-яёіїєґ]/g, (c) => (cyrillicMap[c] ?? ''));

  // Step 3: Normalize Unicode (handles accents and other diacritics)
  sanitized = sanitized.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  
  // Step 4: Remove all non-alphanumeric characters except hyphen and underscore
  sanitized = sanitized.replace(/[^a-z0-9-_]/g, '');
  
  // Step 5: Remove multiple consecutive hyphens/underscores
  sanitized = sanitized.replace(/[-_]+/g, '-');
  
  // Step 6: Trim hyphens/underscores from start and end
  sanitized = sanitized.replace(/^[-_]+|[-_]+$/g, '');
  
  return sanitized;
}

/**
 * Set tenant ID in localStorage with sanitization
 */
export function setTenantId(tenantId: string): string {
  const sanitized = sanitizeTenantId(tenantId);
  localStorage.setItem('tenantId', sanitized);
  console.log('✅ Tenant ID set:', { original: tenantId, sanitized });
  return sanitized;
}

/**
 * Get tenant ID from localStorage
 */
export function getTenantId(): string | null {
  return localStorage.getItem('tenantId');
}

/**
 * Clear tenant ID from localStorage
 */
export function clearTenantId(): void {
  localStorage.removeItem('tenantId');
}
